import { FunctionComponent, useMemo, useRef, useState } from 'react';
import './App.scss';

import { IDanceClass, IScheduleTableFilters, ISelectedClassTrial, SCHEDULE_MODE, SCREEN_SIZE } from './models';
import { ScheduleTable, ScheduleTableFilters, EnrolmentForm, EnrolmentComplete, EnrolFooter, TrialFooter } from './components';
import { useGetDanceSchool, useSubmitEnrolmentFormMutation } from './hooks/apiHooks';
import { useMutationObserver, useQueryParams, useResizeObserver } from './hooks';
import { calculateAgeAtStartOfTerm, classNames, cleanupPhoneNumber, mapSelectedClassFees } from './utils';
import { IBasicAuthDanceSchoolEnrolStudentRequest, IEnrolmentFormData, initEnrolmentFormData } from './models/EnrolmentFormData';

import dayjs from 'dayjs';
import { DEFAULT_COUNTRY_CODE } from './constants/intl';
import { IStudio } from './models/studio';

const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

const initTableFilters = (): IScheduleTableFilters => ({
  age: '',
  dayOfWeek: '',
  style: ''
});

interface IDanceSchoolStudiosColumn {
  index: number;
  studios: IStudio[];
}

export const App: FunctionComponent = () => {

  const mainWrapperRef = useRef<HTMLFormElement>(null);

  const [screenSize, setScreenSize] = useState<SCREEN_SIZE>(SCREEN_SIZE.SMALL);

  const { mode } = useQueryParams<{ mode: SCHEDULE_MODE }>();

  const [wasEnrolmentFormSent, setWasEnrolmentFormSent] = useState(false);
  const [wasEnrolmentError, setWasEnrolmentError] = useState(false);

  const [tableFilters, setTableFilters] = useState<IScheduleTableFilters>(initTableFilters());
  const [enrolmentFormData, setEnrolmentFormData] = useState<IEnrolmentFormData>(initEnrolmentFormData());
  const [selectedEnrolmentStartDate, setSelectedEnrolmentStartDate] = useState<string>('');
  const [selectedTrialDates, setSelectedTrialDates] = useState<ISelectedClassTrial[]>([]);

  const { data: danceSchool, isLoading: danceSchoolLoading, isError: danceSchoolError } = useGetDanceSchool();

  const { isEnrolmentsFinished, isEnrolmentsOpen, enrolmentYear } = useMemo(() => {
    const isEnrolmentsOpen = danceSchool?.latestEnrolmentYearSettings?.yearStarted ?? false;

    const lastTerm = (danceSchool?.latestEnrolmentYearSettings?.termDates ?? []).at(-1);
    const isEnrolmentsFinished = dayjs().isAfter(lastTerm?.endDate ?? dayjs().add(1, 'day'), 'date');

    const enrolmentYear = danceSchool?.latestEnrolmentYearSettings?.year ?? dayjs().year();

    return {
      isEnrolmentsOpen,
      isEnrolmentsFinished,
      enrolmentYear
    }
  }, [danceSchool]);

  const [selectedClasses, setSelectedClasses] = useState<IDanceClass[]>([]);
  const totalSelectedClassesFees = useMemo(() => {
    return mapSelectedClassFees(selectedClasses, danceSchool?.countryCode ?? DEFAULT_COUNTRY_CODE).filter(x => !x.noCharge).reduce((total, item) => total += item.fee, 0);
  }, [selectedClasses, danceSchool]);

  const danceSchoolStudioColumns = useMemo<IDanceSchoolStudiosColumn[]>(() => {
    if (!danceSchool?.studios || danceSchool.studios.length < 1) {
      return [];
    }

    let maxColumns = 1;
    switch (screenSize) {
      case SCREEN_SIZE.LARGE: maxColumns = 2; break;
      case SCREEN_SIZE.EXTRA_LARGE: maxColumns = 3; break;
    }

    let currentColumnIndex = 0;
    const columns: IDanceSchoolStudiosColumn[] = [];
    for (const studio of danceSchool.studios) {
      const studioColumn = columns[currentColumnIndex] ?? { index: currentColumnIndex, studios: [] };
      studioColumn.studios.push(studio);
      columns[currentColumnIndex] = studioColumn;

      currentColumnIndex++;
      if (currentColumnIndex > maxColumns - 1) {
        currentColumnIndex = 0;
      }
    }

    return columns;
  }, [danceSchool?.studios, screenSize]);

  const { mutate: submitEnrolmentForm, isPending: submitEnrolmentFormLoading } = useSubmitEnrolmentFormMutation(() => {
    setWasEnrolmentFormSent(true);
    postParentFrameMessage({ type: "scrollTop" });
  }, () => {
    setWasEnrolmentError(true);
  });

  const handleRowClicked = (row: IDanceClass): void => {
    if (mode !== SCHEDULE_MODE.ENROL && mode !== SCHEDULE_MODE.TRIAL) {
      return;
    }

    const selectedClassesClone = [...selectedClasses];
    const currentIndex = selectedClasses.indexOf(row);
    if (currentIndex >= 0) {
      selectedClassesClone.splice(currentIndex, 1);
    } else {
      selectedClassesClone.push(row);
    }
    setSelectedClasses(selectedClassesClone);
  };

  const handleEnrolmentFormDataChanged = (updatedForm: IEnrolmentFormData) => {
    setEnrolmentFormData(updatedForm);

    let hasValidDateOfBirth = false;
    if (updatedForm.studentDateOfBirth) {
      const dateOfBirth = dayjs(updatedForm.studentDateOfBirth);
      if (dateOfBirth.isValid()) {
        const ageInYears = calculateAgeAtStartOfTerm(dateOfBirth, danceSchool!);
        if (ageInYears < 100 && ageInYears > 0) {
          hasValidDateOfBirth = true;
          if (tableFilters.age !== ageInYears.toString()) {
            setTableFilters(tf => ({...tf, age: ageInYears.toString() }));
          }
        }
      }
    }

    if (!hasValidDateOfBirth) {
      setTableFilters(tf => ({...tf, age: '' }));
    }
  }

  const enrolAnotherStudent = () => {
    setEnrolmentFormData(fd => ({
      ...fd,
      studentFirstName: '',
      studentLastName: '',
      studentDateOfBirth: '',
      medicalRequirements: ''
    }));
    setSelectedClasses([]);
    setTableFilters(initTableFilters());
    setWasEnrolmentFormSent(false);
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const requestBody: IBasicAuthDanceSchoolEnrolStudentRequest = {
      ...enrolmentFormData,
      contactPhoneNumber: cleanupPhoneNumber(enrolmentFormData.contactPhoneNumber),
      enrolAsTrial: mode === SCHEDULE_MODE.TRIAL,
      preCalculatedDiscountedTermCost: totalSelectedClassesFees,
      selectedDanceClasses: selectedClasses.map(x => ({ danceClassId: x.id })),
    };

    setWasEnrolmentError(false);
    if (mode === SCHEDULE_MODE.ENROL) {
      requestBody.enrolmentStartDate = selectedEnrolmentStartDate || undefined;
    } else if (mode === SCHEDULE_MODE.TRIAL) {
      requestBody.selectedDanceClasses = selectedTrialDates.map(x => ({
        danceClassId: x.classDetails.id,
        trialDate: x.selectedTrialDate
      }));
    }

    submitEnrolmentForm(requestBody);
  }

  const postParentFrameMessage = (data: any) => {
    window.parent.postMessage(
      data,
      "*"
    );
  }

  useMutationObserver(() => {
    // Crazy hack to overcome iFrame restrictions
    for (let i = 0; i <= 800; i += 50) {
      setTimeout(() => {
        const height = document.body.scrollHeight;
        postParentFrameMessage({ type: "setHeight", height });
      }, i)
    }
  }, []);

  useResizeObserver(() => {
    if (document.body.clientWidth < 600) {
      setScreenSize(SCREEN_SIZE.SMALL)
    } else if (document.body.clientWidth < 900) {
      setScreenSize(SCREEN_SIZE.MEDIUM);
    } else if (document.body.clientWidth < 1440) {
      setScreenSize(SCREEN_SIZE.LARGE);
    } else {
      setScreenSize(SCREEN_SIZE.EXTRA_LARGE)
    }
  }, []);

  return (
    <form
      className={classNames('ppa-schedule-embed', {
        'dev-mode': isDevMode
      })}
      ref={mainWrapperRef}
      onSubmit={handleSubmit}
    >
      {danceSchoolLoading ? (
        <div className='loading-wrapper'>
          <div className='loading-spinner'></div>
        </div>
      ) : isEnrolmentsFinished ? (
        <div className='alert-wrapper'>
          <h2>Enrolments for {enrolmentYear} are now closed.</h2>
          <br/>
          <h2>Please check back soon for {enrolmentYear + 1} enrolments ✨</h2>
        </div>
      ) : !isEnrolmentsOpen ? (
        <>
          <div className='alert-wrapper'>
            <h2>Enrolments for {enrolmentYear} are not open at this time.</h2>
            <br/>
            <h2>Please check back soon for updates ✨</h2>
          </div>
        </>
      ) : wasEnrolmentFormSent ? (
        <EnrolmentComplete
          onEnrolAnotherStudent={enrolAnotherStudent}
          mode={mode}
        />
      ) : (
        <>
          {(mode === SCHEDULE_MODE.ENROL || mode === SCHEDULE_MODE.TRIAL) && (
            <EnrolmentForm
              formData={enrolmentFormData}
              formDataChanged={handleEnrolmentFormDataChanged}
              screenSize={screenSize}
              disabled={submitEnrolmentFormLoading}
            />
          )}

          <ScheduleTableFilters
            mode={mode}
            filters={tableFilters}
            filtersChanged={setTableFilters}
            screenSize={screenSize}
            disabled={submitEnrolmentFormLoading}
          />
    
          {danceSchoolError || !danceSchool ? (
            <div className='alert-wrapper'>
              <h2 className='color-error'>Something went wrong while trying to load the schedule. Please try again later</h2>
            </div>
          ) : (
            <>
              <div className={classNames('schedule-wrapper')}>
                {danceSchoolStudioColumns.map((x) => (
                  <div key={x.index} className='studio-column'>
                    {x.studios.map(studio => (
                      <div key={studio.id} className='studio-wrapper'>
                        <h3>{studio.name}</h3>
          
                        <ScheduleTable
                          mode={mode}
                          danceSchool={danceSchool}
                          screenSize={screenSize}
                          classes={(danceSchool.danceClasses).filter(x => x.studioId === studio.id)}
                          selectedClasses={selectedClasses}
                          classClicked={handleRowClicked}
                          filters={tableFilters}
                          disabled={submitEnrolmentFormLoading}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {mode === SCHEDULE_MODE.ENROL ? (
                <EnrolFooter
                  danceSchool={danceSchool}
                  selectedClasses={selectedClasses}
                  screenSize={screenSize}
                  disabled={submitEnrolmentFormLoading}
                  isError={wasEnrolmentError}
                  onSelectedEnrolmentStartDateChanged={setSelectedEnrolmentStartDate}
                />
              ) : mode === SCHEDULE_MODE.TRIAL ? (
                <TrialFooter
                  danceSchool={danceSchool}
                  selectedClasses={selectedClasses}
                  screenSize={screenSize}
                  disabled={submitEnrolmentFormLoading}
                  isError={wasEnrolmentError}
                  onSelectedTrialDatesChanged={setSelectedTrialDates}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </form>
  );
}
